/**
 * CreditEngineCorpusHealth — /admin/credit-engine/corpus-health
 *
 * Wave 5-Late Credit Engine propagation — anchor pattern applied.
 * `pctBadge` returns a `Badge` tone + label pair (was a Tailwind class
 * string). Header now uses AdminLayout `title` + `subtitle` (removes
 * inline h1). Trigger button ("Run now") is the token AccentButton.
 * Traffic-light thresholds (95 / 75 / 0) preserved BYTE-EXACT.
 *
 * Business logic (regression run persist + reload) preserved BYTE-EXACT.
 * All testids unchanged.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Play, CheckCircle2, XCircle, AlertTriangle, ListTree } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import { api, formatApiError } from "@/api/client";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, Stat } from "@/admin/_primitives";

/* pctBadge — tone map preserves the three thresholds byte-exact:
   >=95 → successMuted, >=75 → warning, >0 → danger, ==0/null → neutralMuted. */
function pctBadge(v) {
  if (v === undefined || v === null) return { tone: "neutralMuted", label: "—" };
  const n = Number(v);
  if (n >= 95) return { tone: "successMuted", label: `${n.toFixed(1)}%` };
  if (n >= 75) return { tone: "warning", label: `${n.toFixed(1)}%` };
  if (n > 0)   return { tone: "warning", label: `${n.toFixed(1)}%` };
  return { tone: "danger", label: `${n.toFixed(1)}%` };
}





export default function AdminCreditEngineCorpusHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/credit-engine/corpus-health");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function runNow() {
    setRunning(true);
    try {
      await api.post("/admin/credit-engine/corpus-health/run-now");
      toast.success("Regression run persisted");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  }

  const per = data?.per_entry || [];
  const summary = data?.summary || {};
  const run = data?.run || {};

  return (
    <AdminLayout
      title="Credit engine corpus health"
      subtitle="Phase 5C — 20-entry regression corpus, measurement mode. Nightly cron @ 03:05 UTC. Direct URL only, not in nav."
      actions={
        <div className="flex gap-2">
          <GhostButton onClick={reload} data-testid="admin-corpus-reload-btn" aria-label="Reload" className="!py-1 !text-[11px]">
            <RefreshCw className="w-3 h-3" /> Reload
          </GhostButton>
          <AccentButton
            onClick={runNow}
            disabled={running}
            data-testid="admin-corpus-run-now-btn"
          >
            <Play className="w-3 h-3" />
            {running ? "Running…" : "Run now"}
          </AccentButton>
        </div>
      }
    >
      <div data-testid="admin-corpus-health">
        {loading ? (
          <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
            Loading latest run…
          </div>
        ) : !run?.run_id ? (
          <div
            className="text-[13px] py-6 text-center"
            style={{ color: "var(--ds-color-ink-muted)" }}
            data-testid="admin-corpus-empty"
          >
            No regression run yet. Click "Run now" to produce the first one.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <Stat
                testid="admin-corpus-total-entries"
                label="Total entries"
                value={summary.total_entries || per.length}
                highlight
              />
              <Stat
                label="Avg extraction"
                value={`${(summary.avg_extraction_accuracy_pct ?? 0).toFixed(1)}%`}
              />
              <Stat
                label="Avg negative-detect"
                value={`${(summary.avg_negative_detection_accuracy_pct ?? 0).toFixed(1)}%`}
              />
              <Stat
                label="Bureau preservation"
                value={`${summary.bureau_preservation_pass_count ?? 0}/${summary.total_entries ?? per.length}`}
              />
            </div>

            <div
              className="text-[11px] mb-3 tabular-nums font-mono"
              style={{ color: "var(--ds-color-ink-muted)" }}
              data-testid="admin-corpus-run-meta"
            >
              Last run: {run.run_id} · started {(run.started_at || "").slice(0, 19)} · duration {run.duration_ms}ms · corpus {run.corpus_version}
            </div>

            {summary.worst_entry && (
              <div
                className="mb-3 flex flex-wrap items-center gap-3 text-[12px] px-3 py-2 rounded-[var(--ds-radius-input)]"
                style={{
                  background: "var(--ds-color-surface)",
                  border: "1px solid var(--ds-color-admin-border)",
                  color: "var(--ds-color-ink)",
                }}
              >
                <span className="inline-flex items-center gap-1" style={{ color: "var(--ds-color-warning)" }}>
                  <AlertTriangle className="w-3.5 h-3.5" /> Worst
                </span>
                <code className="font-mono text-[11px]">{summary.worst_entry.entry_id}</code>
                <span className="tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                  @ {summary.worst_entry.extraction_accuracy_pct}%
                </span>
                <span style={{ color: "var(--ds-color-ink-subtle)" }}>·</span>
                <span className="inline-flex items-center gap-1" style={{ color: "var(--ds-color-success-muted)" }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Best
                </span>
                <code className="font-mono text-[11px]">{summary.best_entry.entry_id}</code>
                <span className="tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                  @ {summary.best_entry.extraction_accuracy_pct}%
                </span>
              </div>
            )}

            <Section
              title="Per-entry regression"
              icon={ListTree}
              testid="admin-corpus-per-entry-section"
              bodyClassName="p-0"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]" data-testid="admin-corpus-per-entry-table">
                  <thead>
                    <tr
                      className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                      style={{
                        color: "var(--ds-color-ink-subtle)",
                        background: "var(--ds-color-admin-surface-muted)",
                        borderBottom: "1px solid var(--ds-color-admin-border)",
                      }}
                    >
                      <th className="px-4 py-2 font-semibold">Entry</th>
                      <th className="px-4 py-2 font-semibold">Category</th>
                      <th className="px-4 py-2 font-semibold">Extraction</th>
                      <th className="px-4 py-2 font-semibold">Neg-detect</th>
                      <th className="px-4 py-2 font-semibold">Bureau</th>
                      <th className="px-4 py-2 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {per.map((e, idx) => {
                      const ext = pctBadge(e.extraction_accuracy_pct);
                      const neg = pctBadge(e.negative_detection_accuracy_pct);
                      const isLast = idx === per.length - 1;
                      return (
                        <tr
                          key={e.entry_id}
                          data-testid={`admin-corpus-row-${e.entry_id}`}
                          style={{
                            borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                          }}
                        >
                          <td
                            className="px-4 py-1.5 font-mono text-[12px]"
                            style={{ color: "var(--ds-color-ink)" }}
                          >
                            {e.entry_id}
                          </td>
                          <td
                            className="px-4 py-1.5 text-[12px]"
                            style={{ color: "var(--ds-color-ink-muted)" }}
                          >
                            {e.category}
                          </td>
                          <td className="px-4 py-1.5">
                            <Badge tone={ext.tone} size="sm" shape="sharp" className="tabular-nums">
                              {ext.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-1.5">
                            <Badge tone={neg.tone} size="sm" shape="sharp" className="tabular-nums">
                              {neg.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-1.5">
                            {e.bureau_preservation_pass ? (
                              <CheckCircle2
                                className="w-3.5 h-3.5"
                                style={{ color: "var(--ds-color-success-muted)" }}
                              />
                            ) : (
                              <XCircle
                                className="w-3.5 h-3.5"
                                style={{ color: "var(--ds-color-danger)" }}
                              />
                            )}
                          </td>
                          <td
                            className="px-4 py-1.5 text-[12px]"
                            style={{ color: "var(--ds-color-ink-muted)" }}
                          >
                            {e.notes || ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
